import type { Lang } from "./types";

/**
 * Every word the partner map page says by itself, in each language it speaks (`lang` in the content file, English when unset). What
 * is the partner's own to say (names, blurbs, offers, the venue's tagline) stays in the content file, in the page's language.
 * The Dutch is the designer's where the In de Waag prototype has the string (`indewaag-nl.html`), ours where it has none: those marked
 * "ours" want a Dutch reader's eye before a Dutch hostel or hotel uses them.
 */

export interface Strings {
	/** the intl locale: dates and lists keep the en-GB the pages always had */
	locale: string;
	tabs: { highlights: string; offers: string; contact: string; week: string };
	/** "Save this map" in the rail's foot, its short form beside a second button, and the short form's accessible name */
	saveThisMap: string;
	saveMap: string;
	saveMapLabel: string;
	freeInStippl: string;
	freeInStipplWeek: string;
	legendHighlights: string;
	onToday: string;
	onDay: (day: string) => string;
	zoomIn: string;
	zoomOut: string;
	backToHostel: string;
	backTo: (name: string) => string;
	close: string;
	details: string;
	// distances: the list's row writes the number and `minWalkSuffix` as two pieces, as the page always did
	minWalkSuffix: string;
	minWalk: (min: number) => string;
	kmAway: (km: string) => string;
	// a place beyond the content's `bikeFromKm` is a ride
	minBike: (min: number) => string;
	/** under the minutes of a row: what the time is of, "walk" or "by bike" */
	walkWord: string;
	byBike: string;
	walkFrom: (walk: string, name: string) => string;
	// the Highlights tab
	aroundTheCorner: string;
	shortWalk: string;
	walkFromHostel: string;
	worthTheRide: string;
	yourBase: string;
	bookInfo: string;
	contactUs: string;
	weekProgram: string;
	// the ways to reach the partner
	bookBed: string;
	whatsapp: string;
	call: string;
	website: string;
	directions: string;
	allEvents: string;
	hostelPage: string;
	events: string;
	hostel: string;
	onNetwork: (name: string, network: string) => string;
	tonightAtHostel: string;
	nextStops: string;
	// the small print
	mapMadeWith: string;
	tripPlanner: string;
	hostelSmallPrint: string;
	// the Week program
	dayOfWeek: string;
	days: string[];
	today: string;
	thingsOn: (n: number) => string;
	alsoAt: (times: string) => string;
	atHostel: string;
	dayTripsOn: (day: string) => string;
	useCodeBefore: string;
	useCodeAfter: (discount: string) => string;
	discountOff: (discount: string) => string;
	comingUp: string;
	toursTrips: string;
	book: string;
	options: (n: number) => string;
	showAllTours: (name: string) => string;
	fromTime: (time: string) => string;
	oneNightOnly: string;
	free: string;
	// the detail sheet
	startPlanning: string;
	signUpWhatsapp: string;
	orSignUp: string;
	venue: string;
	greeting: (name: string) => string;
	signupMessage: (name: string, event: string, date: string, time: string) => string;
	// a restaurant or bar: what it offers, where to book, who its sister is
	bookTable: string;
	bookShort: string;
	menu: string;
	seeAll: string;
	see: string;
	fromPrice: (price: string) => string;
	afterThis: (title: string) => string;
	minBackTo: (min: number, area: string) => string;
	minBackToBike: (min: number, area: string) => string;
	savePlan: string;
	bookAndContact: string;
	sisterSection: string;
	sister: string;
	bookAt: (name: string) => string;
	seeOnMap: string;
	onMap: string;
	highlightsBelow: (n: number) => string;
	waysToEat: (n: number) => string;
	reviewsOn: (n: number, source: string) => string;
	address: string;
	openingHours: string;
	phone: string;
	email: string;
	// the map's credits
	photos: string;
	expandAttribution: string;
	collapseAttribution: string;
}

const EN: Strings = {
	locale: "en-GB",
	tabs: { highlights: "Highlights", offers: "Eat & drink here", contact: "Contact", week: "Week program" },
	saveThisMap: "Save this map",
	saveMap: "Save map",
	saveMapLabel: "Save this map in Stippl",
	freeInStippl: "Free in Stippl · the map and every place on it",
	freeInStipplWeek: "Free in Stippl · map, days and bookings in one place",
	legendHighlights: "Highlights",
	onToday: "On today",
	onDay: (day) => `On ${day}`,
	zoomIn: "Zoom in",
	zoomOut: "Zoom out",
	backToHostel: "Back to the hostel",
	backTo: (name) => `Back to ${name}`,
	close: "Close",
	details: "Details",
	minWalkSuffix: " min walk",
	minWalk: (min) => `${min} min walk`,
	kmAway: (km) => `${km} km away`,
	minBike: (min) => `${min} min by bike`,
	walkWord: "walk",
	byBike: "by bike",
	walkFrom: (walk, name) => `${walk} from ${name}`,
	aroundTheCorner: "Around the corner",
	shortWalk: "A short walk",
	walkFromHostel: "Walk from the hostel",
	worthTheRide: "Worth the ride",
	yourBase: "Your base",
	bookInfo: "Book & info",
	contactUs: "Contact us",
	weekProgram: "Week program",
	bookBed: "Book a bed",
	whatsapp: "WhatsApp",
	call: "Call",
	website: "Website",
	directions: "Directions",
	allEvents: "All events",
	hostelPage: "Hostel page",
	events: "Events",
	hostel: "Hostel",
	onNetwork: (name, network) => `${name} on ${network}`,
	tonightAtHostel: "Tonight at the hostel",
	nextStops: "Next stops:",
	mapMadeWith: "Map made with",
	tripPlanner: ", the free trip planner, for ",
	hostelSmallPrint:
		"Times and prices come from the hostel and can change, so reception has the latest. Shows, tours and day trips are booked directly with their organisers.",
	dayOfWeek: "Day of the week",
	days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
	today: "Today",
	thingsOn: (n) => `${n} ${n === 1 ? "thing" : "things"} on`,
	alsoAt: (times) => `Also at ${times}`,
	atHostel: "At the hostel",
	dayTripsOn: (day) => `Day trips on ${day}`,
	useCodeBefore: "Use code ",
	useCodeAfter: (discount) => ` for ${discount} off any day trip`,
	discountOff: (discount) => `${discount} off`,
	comingUp: "Coming up",
	toursTrips: "Tours & trips",
	book: "Book",
	options: (n) => `${n} options`,
	showAllTours: (name) => `Show all tours on ${name}`,
	fromTime: (time) => `from ${time}`,
	oneNightOnly: "one night only",
	free: "Free",
	startPlanning: "Start planning",
	signUpWhatsapp: "Sign up on WhatsApp",
	orSignUp: "Or sign up at reception ·",
	venue: "Venue",
	greeting: (name) => `Hi ${name}! `,
	signupMessage: (name, event, date, time) => `Hi ${name}! I'd like to sign up for ${event} on ${date} (${time}).`,
	bookTable: "Book a table",
	bookShort: "Book",
	menu: "Menu",
	seeAll: "See all",
	see: "See",
	fromPrice: (price) => `from ${price}`,
	afterThis: (title) => `After this: ${title}`,
	minBackTo: (min, area) => `${min} min back to the ${area}`,
	minBackToBike: (min, area) => `${min} min by bike back to the ${area}`,
	savePlan: "Save to plan",
	bookAndContact: "Book & contact",
	sisterSection: "Our sister restaurant",
	sister: "Sister restaurant",
	bookAt: (name) => `Book at ${name}`,
	seeOnMap: "See on the map",
	onMap: "On the map",
	highlightsBelow: (n) => `${n} highlights below`,
	waysToEat: (n) => `${n} ways to eat & drink`,
	reviewsOn: (n, source) => `${n} reviews on ${source}`,
	address: "Address",
	openingHours: "Opening hours",
	phone: "Phone",
	email: "Email",
	photos: "Photos:",
	expandAttribution: "Expand map attribution",
	collapseAttribution: "Collapse map attribution",
};

const NL: Strings = {
	locale: "nl-NL",
	tabs: { highlights: "Highlights", offers: "Eten & drinken", contact: "Contact", week: "Weekprogramma" },
	saveThisMap: "Bewaar deze kaart",
	saveMap: "Kaart bewaren",
	saveMapLabel: "Bewaar deze kaart in Stippl",
	freeInStippl: "Gratis in Stippl · de kaart en elke plek erop",
	freeInStipplWeek: "Gratis in Stippl · kaart, dagen en boekingen op één plek",
	legendHighlights: "Highlights",
	onToday: "Vandaag",
	onDay: (day) => `Op ${day}`,
	zoomIn: "Inzoomen",
	zoomOut: "Uitzoomen",
	backToHostel: "Terug naar het hostel",
	backTo: (name) => `Terug naar ${name}`,
	close: "Sluiten",
	details: "Details",
	minWalkSuffix: " min lopen",
	minWalk: (min) => `${min} min lopen`,
	kmAway: (km) => `${km} km verderop`,
	minBike: (min) => `${min} min fietsen`,
	walkWord: "lopen",
	byBike: "fietsen",
	walkFrom: (walk, name) => `${walk} vanaf ${name}`,
	aroundTheCorner: "Om de hoek",
	shortWalk: "Een kort stukje lopen",
	walkFromHostel: "Lopen vanaf het hostel",
	worthTheRide: "De rit waard",
	yourBase: "Jouw uitvalsbasis",
	bookInfo: "Boeken & info",
	contactUs: "Neem contact op",
	weekProgram: "Weekprogramma",
	bookBed: "Boek een bed",
	whatsapp: "WhatsApp",
	call: "Bel",
	website: "Website",
	directions: "Route",
	allEvents: "Alle evenementen",
	hostelPage: "Hostelpagina",
	events: "Evenementen",
	hostel: "Hostel",
	onNetwork: (name, network) => `${name} op ${network}`,
	tonightAtHostel: "Vanavond in het hostel",
	nextStops: "Volgende stops:",
	mapMadeWith: "Kaart gemaakt met",
	tripPlanner: ", de gratis reisplanner, voor ",
	hostelSmallPrint:
		"Tijden en prijzen komen van het hostel en kunnen veranderen, dus de receptie heeft de actuele informatie. Shows, tours en dagtrips boek je rechtstreeks bij de organisatoren.",
	dayOfWeek: "Dag van de week",
	days: ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"],
	today: "Vandaag",
	thingsOn: (n) => `${n} ${n === 1 ? "activiteit" : "activiteiten"}`,
	alsoAt: (times) => `Ook om ${times}`,
	atHostel: "In het hostel",
	dayTripsOn: (day) => `Dagtrips op ${day.toLowerCase()}`,
	useCodeBefore: "Gebruik code ",
	useCodeAfter: (discount) => ` voor ${discount} korting op elke dagtrip`,
	discountOff: (discount) => `${discount} korting`,
	comingUp: "Binnenkort",
	toursTrips: "Tours & trips",
	book: "Boek",
	options: (n) => `${n} opties`,
	showAllTours: (name) => `Alle tours bij ${name} bekijken`,
	fromTime: (time) => `vanaf ${time}`,
	oneNightOnly: "één avond",
	free: "Gratis",
	startPlanning: "Begin met plannen",
	signUpWhatsapp: "Aanmelden via WhatsApp",
	orSignUp: "Of meld je aan bij de receptie ·",
	venue: "Locatie",
	greeting: (name) => `Hoi ${name}! `,
	signupMessage: (name, event, date, time) => `Hoi ${name}! Ik wil me graag aanmelden voor ${event} op ${date} (${time}).`,
	bookTable: "Tafel reserveren",
	bookShort: "Reserveer",
	menu: "Menu",
	seeAll: "Bekijk alles",
	see: "Bekijk",
	fromPrice: (price) => `vanaf ${price}`,
	afterThis: (title) => `Daarna: ${title}`,
	minBackTo: (min, area) => `${min} min terug naar de ${area}`,
	minBackToBike: (min, area) => `${min} min fietsen terug naar de ${area}`,
	savePlan: "Bewaar in je plan",
	bookAndContact: "Reserveren & contact",
	sisterSection: "Ons zusterrestaurant",
	sister: "Zusterrestaurant",
	bookAt: (name) => `Reserveer bij ${name}`,
	seeOnMap: "Bekijk op de kaart",
	onMap: "Op de kaart",
	highlightsBelow: (n) => `${n} plekken hieronder`,
	waysToEat: (n) => `${n} manieren om te eten & drinken`,
	reviewsOn: (n, source) => `${n} reviews op ${source}`,
	address: "Adres",
	openingHours: "Openingstijden",
	phone: "Telefoon",
	email: "E-mail",
	photos: "Foto's:",
	expandAttribution: "Bronnen van de kaart tonen",
	collapseAttribution: "Bronnen van de kaart verbergen",
};

const STRINGS: Record<Lang, Strings> = { en: EN, nl: NL };

/** The words of a page's language: `strings(content.lang)`. */
export const strings = (lang: Lang = "en"): Strings => STRINGS[lang];

const numberFormats = new Map<string, Intl.NumberFormat>();
function numberFormat(lang: Lang, digits: "fixed" | "trim"): Intl.NumberFormat {
	const key = `${lang}/${digits}`;
	let format = numberFormats.get(key);
	if (!format) {
		format = new Intl.NumberFormat(strings(lang).locale, { minimumFractionDigits: digits === "fixed" ? 1 : 0, maximumFractionDigits: 1, useGrouping: false });
		numberFormats.set(key, format);
	}
	return format;
}

/** "0.8" in English, "0,8" in Dutch: one decimal, kept ("3.0"). A list row's distance. */
export const kmFixed = (km: number, lang: Lang = "en"): string => numberFormat(lang, "fixed").format(km);

/** One decimal, and none when it is a whole number: "2.4", "3". The distance in "2.4 km away". */
export const kmTrimmed = (km: number, lang: Lang = "en"): string => numberFormat(lang, "trim").format(km);
